import {
  createChunkLoadRecoveryLifecycle,
  currentBuildKey,
  DEVELOPMENT_BUILD_KEY,
  type ChunkLoadRecoveryLifecycle,
  type ChunkLoadRecoveryOutcome,
} from "./chunk-load-recovery";
import { FRESH_BUILD_PARAM, freshBuildReason, freshBuildUrl } from "@shared/deployment/page-cache-identity";

export type RetiredDeploymentOutcome =
  | "reloading"
  | "exhausted"
  | "storage_unavailable"
  | "deployment_current"
  | "deployment_unknown";

const DOCUMENT_RELEASE_ATTRIBUTE = "data-release";

export function documentReleaseAttribute(): Record<string, string> {
  return { [DOCUMENT_RELEASE_ATTRIBUTE]: currentBuildKey() };
}

let documentReleaseIsRetired = false;

export function isRetiredDocumentRelease(
  documentRelease: string | null,
  bundleRelease: string,
): boolean {
  if (bundleRelease === DEVELOPMENT_BUILD_KEY) return false;
  if (documentRelease === null) return false;
  return documentRelease !== bundleRelease;
}

export function reloadServingBuild(): void {
  window.location.replace(freshBuildUrl(window.location.href));
}

function loadedAfterStaleAction(): boolean {
  if (typeof window === "undefined") return false;
  return freshBuildReason(new URLSearchParams(window.location.search).get(FRESH_BUILD_PARAM)) === "stale-action";
}

let staleActionReloaded = loadedAfterStaleAction();

export function takeStaleActionReload(): boolean {
  const reloaded = staleActionReloaded;
  staleActionReloaded = false;
  return reloaded;
}

function forgetFreshBuildParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(FRESH_BUILD_PARAM)) return;
  url.searchParams.delete(FRESH_BUILD_PARAM);
  window.history.replaceState(window.history.state, "", url);
}

export function snapshotDocumentRelease(): void {
  if (typeof document === "undefined") return;
  forgetFreshBuildParam();
  documentReleaseIsRetired = isRetiredDocumentRelease(
    document.documentElement.getAttribute(DOCUMENT_RELEASE_ATTRIBUTE),
    currentBuildKey(),
  );
}

export function attemptMismatchedDocumentRecovery(
  beforeReload?: () => void,
): ChunkLoadRecoveryOutcome {
  if (!documentReleaseIsRetired || typeof window === "undefined") {
    return "not_chunk_load_error";
  }
  try {
    return createChunkLoadRecoveryLifecycle(window.sessionStorage, reloadServingBuild).reloadOnce(window.location.pathname, beforeReload);
  } catch {
    return "storage_unavailable";
  }
}

const SERVING_RELEASE_PROBE_PATH = "/api/health";
const SERVING_RELEASE_TIMEOUT_MILLISECONDS = 2_000;

export type ReleaseProbeFetch = (
  input: string,
  init: RequestInit,
) => Promise<{ ok: boolean; json: () => Promise<unknown> }>;

async function readServingRelease(
  fetchImpl: ReleaseProbeFetch,
): Promise<string | undefined> {
  try {
    const response = await fetchImpl(SERVING_RELEASE_PROBE_PATH, {
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(SERVING_RELEASE_TIMEOUT_MILLISECONDS),
    });
    if (!response.ok) return undefined;
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null) return undefined;
    if (!("releaseSha" in body)) return undefined;
    const { releaseSha } = body;
    return typeof releaseSha === "string" && releaseSha.length > 0
      ? releaseSha
      : undefined;
  } catch {
    return undefined;
  }
}

export type ReleaseFreshness = "current" | "outdated" | "unknown";

export async function readReleaseFreshness(
  bundleRelease: string,
  fetchImpl: ReleaseProbeFetch,
): Promise<ReleaseFreshness> {
  if (bundleRelease === DEVELOPMENT_BUILD_KEY) return "unknown";
  const servingRelease = await readServingRelease(fetchImpl);
  if (servingRelease === undefined) return "unknown";
  return servingRelease === bundleRelease ? "current" : "outdated";
}

interface RetiredDeploymentProbe {
  bundleRelease: string;
  fetchImpl: ReleaseProbeFetch;
  lifecycle: ChunkLoadRecoveryLifecycle;
  pathname: string;
  beforeReload?: () => void;
}

export async function resolveRetiredDeploymentRecovery({
  bundleRelease,
  fetchImpl,
  lifecycle,
  pathname,
  beforeReload,
}: RetiredDeploymentProbe): Promise<RetiredDeploymentOutcome> {
  const freshness = await readReleaseFreshness(bundleRelease, fetchImpl);
  if (freshness === "unknown") return "deployment_unknown";
  if (freshness === "current") return "deployment_current";
  return lifecycle.reloadOnce(pathname, beforeReload);
}

const RECOVERY_PROBE_INTERVAL_MILLISECONDS = 30_000;

export interface RetiredDeploymentRecovery {
  attempt(beforeReload?: () => void): Promise<RetiredDeploymentOutcome>;
}

export function createRetiredDeploymentRecovery(
  probe: (beforeReload?: () => void) => Promise<RetiredDeploymentOutcome>,
  now: () => number,
  intervalMilliseconds = RECOVERY_PROBE_INTERVAL_MILLISECONDS,
): RetiredDeploymentRecovery {
  let inFlight: Promise<RetiredDeploymentOutcome> | undefined;
  let reloadCommitted = false;
  let answeredAt: number | undefined;

  return {
    attempt(beforeReload) {
      if (reloadCommitted) return Promise.resolve("reloading");
      if (inFlight) return inFlight;
      if (
        answeredAt !== undefined &&
        now() - answeredAt < intervalMilliseconds
      ) {
        return Promise.resolve("deployment_unknown");
      }
      inFlight = probe(beforeReload)
        .catch((): RetiredDeploymentOutcome => "deployment_unknown")
        .then((outcome) => {
          if (outcome === "reloading") reloadCommitted = true;
          answeredAt = now();
          inFlight = undefined;
          return outcome;
        });
      return inFlight;
    },
  };
}

const browserRecovery = createRetiredDeploymentRecovery(
  (beforeReload) =>
    resolveRetiredDeploymentRecovery({
      bundleRelease: currentBuildKey(),
      fetchImpl: (input, init) => fetch(input, init),
      lifecycle: createChunkLoadRecoveryLifecycle(window.sessionStorage, reloadServingBuild),
      pathname: window.location.pathname,
      beforeReload,
    }),
  () => Date.now(),
);

export function attemptRetiredDeploymentRecovery(
  beforeReload?: () => void,
): Promise<RetiredDeploymentOutcome> {
  if (typeof window === "undefined") {
    return Promise.resolve("deployment_unknown");
  }
  return browserRecovery.attempt(beforeReload);
}
